import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { beneficiaries as benApi } from "../api/client";
import type { BankScheme, Beneficiary, Relationship, SettlementRail } from "../api/types";
import { companyCanWrite, useMe } from "../auth/session";
import { Page } from "../components/Shell";
import { Empty, ErrorBox, Field, Icon, Modal, Pill, Spinner, errorMessage, useToast } from "../components/ui";
import { useAsync } from "../hooks/useAsync";
import { fmt, t } from "../i18n";
import { dateShort } from "../lib/format";
import { countryName, railDestination, railLabel, railSla } from "../lib/rail";
import { isValidAddress, isValidBic, isValidEmail, isValidIban, isValidRoutingNumber, type Network } from "../lib/validators";

const STATUS_TONE: Record<string, "ok" | "warn" | "crit"> = { active: "ok", pending_review: "warn", blocked: "crit" };

export function Fornecedores({ openNew = false }: { openNew?: boolean }) {
  const me = useMe();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useAsync(() => benApi.list(), []);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState<Beneficiary | null>(null);
  const [editing, setEditing] = useState<Beneficiary | null>(null);
  const canWrite = companyCanWrite(me);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = data ?? [];
    if (!s) return all;
    return all.filter((b) => [b.legalName, b.country, countryName(b.country), railLabel(b.rail), b.rail.kind === "stablecoin" ? b.rail.address : ""].join(" ").toLowerCase().includes(s));
  }, [data, q]);

  function history(b: Beneficiary): string {
    if (b.status === "blocked") return t.suppliers.flagged;
    if (b.status === "pending_review" && b.dataChangedAt) return fmt(t.suppliers.changedAt, { date: dateShort(b.dataChangedAt) });
    const n = b.paymentsCount ?? 0;
    return n === 0 ? t.suppliers.noPayments : n === 1 ? t.suppliers.payment1 : fmt(t.suppliers.payments, { n });
  }

  return (
    <Page
      title={t.suppliers.title}
      backTo={openNew ? "/fornecedores" : undefined}
      actions={
        canWrite && !openNew ? (
          <Link to="/fornecedores/novo" className="btn">
            {Icon.plus()}
            {t.suppliers.new}
          </Link>
        ) : undefined
      }
    >
      <div className="two n400">
        <div className={`card ${openNew ? "mob-hide" : ""}`}>
          <div className="card-head" style={{ padding: "14px 20px" }}>
            <div className="inp" style={{ minHeight: 38, width: 320, maxWidth: "100%" }}>
              <input aria-label={t.suppliers.searchPh} placeholder={t.suppliers.searchPh} value={q} onChange={(e) => setQ(e.target.value)} style={{ fontSize: 13 }} />
            </div>
            <Pill tone="mute">{fmt(t.suppliers.count, { n: list.length })}</Pill>
          </div>
          {loading && !data ? (
            <Spinner />
          ) : error ? (
            <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
          ) : list.length === 0 ? (
            <Empty body={t.suppliers.empty} />
          ) : (
            <>
              <div className="tscroll desk-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t.suppliers.colName}</th>
                      <th>{t.suppliers.colRail}</th>
                      <th>{t.suppliers.colDest}</th>
                      <th>{t.suppliers.colStatus}</th>
                      <th>{t.suppliers.colHistory}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <div className="cell2">
                            <b>{b.legalName}</b>
                            <span>{countryName(b.country)}</span>
                          </div>
                        </td>
                        <td>{railLabel(b.rail)}</td>
                        <td className="mono ink2">{railDestination(b.rail)}</td>
                        <td><Pill tone={STATUS_TONE[b.status] ?? "warn"}>{t.suppliers.status[b.status] ?? b.status}</Pill></td>
                        <td className="muted">{history(b)}</td>
                        <td className="r">
                          <div className="row" style={{ justifyContent: "flex-end", gap: 12 }}>
                            {canWrite && <button type="button" className="linkbtn muted" onClick={() => setEditing(b)}>{t.suppliers.edit}</button>}
                            {b.status === "active" && canWrite ? (
                              <Link className="linkbtn" to={`/pagamentos/novo?fornecedor=${encodeURIComponent(b.id)}`}>{t.suppliers.pay}</Link>
                            ) : (
                              <button type="button" className="linkbtn muted" onClick={() => setDetail(b)}>
                                {b.status === "blocked" ? t.suppliers.seeReason : t.suppliers.seeReview}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mob-list">
                {list.map((b) => (
                  <button
                    type="button"
                    key={b.id}
                    className="item"
                    style={{ background: "none", border: 0, borderBottom: "1px solid var(--surface-2)", textAlign: "left", width: "100%" }}
                    onClick={() => setDetail(b)}
                  >
                    <div className="l">
                      <b>{b.legalName}</b>
                      <span>{countryName(b.country)} · {railLabel(b.rail)}</span>
                    </div>
                    <div className="r">
                      <Pill tone={STATUS_TONE[b.status] ?? "warn"}>{t.suppliers.status[b.status] ?? b.status}</Pill>
                      <small>{history(b)}</small>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {canWrite && (
          <div className={`card ${openNew ? "" : "mob-hide"}`}>
            <SupplierForm
              onSaved={() => {
                void reload();
                navigate("/fornecedores");
              }}
            />
          </div>
        )}
      </div>
      {detail && (
        <Modal title={detail.legalName} onClose={() => setDetail(null)}>
          <div className="stack s8" style={{ fontSize: 13.5 }}>
            <div className="row"><Pill tone={STATUS_TONE[detail.status] ?? "warn"}>{t.suppliers.status[detail.status]}</Pill><span className="muted">{countryName(detail.country)} · {railLabel(detail.rail)}</span></div>
            <p className="ink2">{detail.status === "blocked" ? t.suppliers.blockedInfo : t.suppliers.reviewInfo}</p>
            {detail.dataChangedAt && <p className="muted">{fmt(t.suppliers.changedAt, { date: dateShort(detail.dataChangedAt) })}</p>}
            {detail.screeningFlags && detail.screeningFlags.length > 0 && (
              <div className="stack s8">
                <span className="lbl">{t.suppliers.flagsTitle}</span>
                <div className="row">{detail.screeningFlags.map((f) => <Pill key={f} tone="warn">{f}</Pill>)}</div>
              </div>
            )}
            <div className="kv"><span className="k">{t.suppliers.colDest}</span><span className="v mono wrap">{detail.rail.kind === "stablecoin" ? detail.rail.address : railDestination(detail.rail)}</span></div>
            <div className="kv"><span className="k">{t.suppliers.slaLabel}</span><span className="v">{railSla(detail.rail)}</span></div>
            {detail.rail.kind === "bank" && <p className="muted" style={{ fontSize: 12.5 }}>{t.suppliers.costNote}</p>}
            {canWrite && (
              <div className="row" style={{ gap: 10, paddingTop: 6 }}>
                {detail.status === "active" && <Link className="btn" to={`/pagamentos/novo?fornecedor=${encodeURIComponent(detail.id)}`}>{t.suppliers.pay}</Link>}
                <button type="button" className="btn ghost" onClick={() => { setEditing(detail); setDetail(null); }}>{t.suppliers.edit}</button>
              </div>
            )}
          </div>
        </Modal>
      )}
      {editing && (
        <Modal title={`${t.suppliers.editTitle} · ${editing.legalName}`} onClose={() => setEditing(null)}>
          <SupplierForm
            initial={editing}
            onSaved={() => {
              setEditing(null);
              void reload();
            }}
          />
        </Modal>
      )}
    </Page>
  );
}

type RailKind = "stablecoin" | "bank";

/** Cria (sem `initial`) ou edita (com `initial`) um fornecedor. Na edição só os campos alterados vão para a API. */
export function SupplierForm({ initial, onSaved }: { initial?: Beneficiary; onSaved: (b: Beneficiary) => void }) {
  const me = useMe();
  const toast = useToast();
  const bankRailOn = me.featureFlags?.bankRail !== false;
  const editing = Boolean(initial);
  const ir = initial?.rail;
  const [legalName, setLegalName] = useState(initial?.legalName ?? "");
  const [country, setCountry] = useState(initial?.country ?? "CN");
  const [address, setAddress] = useState(initial?.address ?? "");
  const [document, setDocument] = useState(initial?.document ?? "");
  const [relationship, setRelationship] = useState<Relationship>(initial?.relationship ?? "goods");
  const [kind, setKind] = useState<RailKind>(ir?.kind ?? "stablecoin");
  const [asset, setAsset] = useState<"USDT" | "USDC">(ir?.kind === "stablecoin" ? ir.asset : "USDT");
  const [network, setNetwork] = useState<Network>(ir?.kind === "stablecoin" ? ir.network : "tron");
  const [walletAddr, setWalletAddr] = useState(ir?.kind === "stablecoin" ? ir.address : "");
  const [bankCountry, setBankCountry] = useState(ir?.kind === "bank" ? ir.country : "US");
  const [scheme, setScheme] = useState<BankScheme>(ir?.kind === "bank" ? ir.scheme : "ach");
  const [account, setAccount] = useState<Record<string, string>>(ir?.kind === "bank" ? { ...(ir.account as Record<string, string>) } : {});
  // CNAPS (yuan) não está disponível: só aparece se o cadastro antigo já estava nele
  const schemes: BankScheme[] = ir?.kind === "bank" && ir.scheme === "cnaps" ? ["ach", "fedwire", "swift", "cnaps"] : ["ach", "fedwire", "swift"];
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addrValid = walletAddr.trim() ? isValidAddress(network, walletAddr) : null;
  const netName = t.suppliers.networks[network] ?? network;

  type AccField = { key: string; label: string; mono?: boolean; optional?: boolean; validate?: (v: string) => string | null; select?: Record<string, string>; autoComplete?: string };
  const val = (k: string) => (account[k] ?? "").trim();
  // identificadores da conta por esquema (o que a Lumx exige no `destination`)
  const idFields: AccField[] = (() => {
    if (scheme === "ach" || scheme === "fedwire") return [
      { key: "routingNumber", label: t.suppliers.routing, mono: true, validate: (v) => (isValidRoutingNumber(v) ? null : t.suppliers.routingInvalid) },
      { key: "accountNumber", label: t.suppliers.accountNumber, mono: true },
      { key: "accountType", label: t.suppliers.accountType, select: t.suppliers.accountTypes },
    ];
    if (scheme === "cnaps") return [{ key: "cnapsCode", label: t.suppliers.cnapsCode, mono: true }, { key: "accountNumber", label: t.suppliers.accountNumber, mono: true }];
    return [
      { key: "swiftBic", label: t.suppliers.swiftBic, mono: true, validate: (v) => (isValidBic(v) ? null : t.suppliers.bicInvalid) },
      { key: "iban", label: t.suppliers.iban, mono: true, optional: true, validate: (v) => (!v || isValidIban(v) ? null : t.suppliers.ibanInvalid) },
      { key: "accountNumber", label: t.suppliers.accountNumber, mono: true, optional: true },
    ];
  })();
  const bankFields: AccField[] = scheme === "cnaps" ? [{ key: "bankName", label: t.suppliers.bankName }] : [
    { key: "bankName", label: t.suppliers.bankName },
    { key: "bankLine1", label: t.suppliers.addrLine1 }, { key: "bankCity", label: t.suppliers.addrCity }, { key: "bankState", label: t.suppliers.addrState }, { key: "bankPostalCode", label: t.suppliers.addrPostalCode },
  ];
  const holderFields: AccField[] = scheme === "cnaps" ? [] : [
    { key: "holderEmail", label: t.suppliers.holderEmail, autoComplete: "email", validate: (v) => (isValidEmail(v) ? null : t.suppliers.holderEmailInvalid) },
    { key: "holderLine1", label: t.suppliers.addrLine1 }, { key: "holderCity", label: t.suppliers.addrCity }, { key: "holderState", label: t.suppliers.addrState }, { key: "holderPostalCode", label: t.suppliers.addrPostalCode },
  ];
  const accountFields: AccField[] = [...idFields, ...bankFields, ...holderFields];
  const fieldError = (f: AccField): string | null => (val(f.key) && f.validate ? f.validate(val(f.key)) : null);

  function bankRailError(): string | null {
    if (accountFields.some((f) => !f.optional && !val(f.key))) return t.suppliers.fillAll;
    if (scheme === "swift" && !val("iban") && !val("accountNumber")) return t.suppliers.ibanOrAccount;
    for (const f of accountFields) { const e = fieldError(f); if (e) return e; }
    if (!document.trim()) return t.suppliers.documentRequiredBank;
    return null;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!legalName.trim() || !country.trim() || (!address.trim() && !editing)) return setError(t.suppliers.fillAll);
    let rail: SettlementRail;
    if (kind === "stablecoin") {
      if (!isValidAddress(network, walletAddr)) return setError(fmt(t.suppliers.walletInvalid, { network: netName }));
      rail = { kind: "stablecoin", asset, network, address: walletAddr.trim() };
    } else {
      const bankErr = bankRailError();
      if (bankErr) return setError(bankErr);
      const known = new Set(accountFields.map((f) => f.key));
      rail = { kind: "bank", country: bankCountry.trim().toUpperCase(), scheme, account: Object.fromEntries(Object.entries(account).map(([k, v]) => [k, v.trim()]).filter(([k, v]) => Boolean(v) && known.has(k as string))) };
      // na edição a API devolve conta/IBAN mascarados: para mudar qualquer dado bancário é preciso redigitar o identificador
      if (initial && JSON.stringify(rail) !== JSON.stringify(initial.rail) && ["accountNumber", "iban"].some((k) => rail.kind === "bank" && rail.account[k]?.startsWith("*"))) return setError(t.suppliers.maskedAccount);
    }
    setPending(true);
    try {
      if (initial) {
        const patch: Parameters<typeof benApi.update>[1] = {};
        if (legalName.trim() !== initial.legalName) patch.legalName = legalName.trim();
        if (country.trim().toUpperCase() !== initial.country) patch.country = country.trim().toUpperCase();
        if (address.trim() && address.trim() !== (initial.address ?? "")) patch.address = address.trim();
        if (document.trim() && document.trim() !== (initial.document ?? "")) patch.document = document.trim();
        if (relationship !== initial.relationship) patch.relationship = relationship;
        if (JSON.stringify(rail) !== JSON.stringify(initial.rail)) patch.rail = rail;
        if (Object.keys(patch).length === 0) { setPending(false); toast(t.suppliers.noChanges); return; }
        const b = await benApi.update(initial.id, patch);
        toast(t.suppliers.saved);
        onSaved(b);
        return;
      }
      const b = await benApi.create({ legalName: legalName.trim(), country: country.trim().toUpperCase(), address: address.trim(), document: document.trim() || undefined, relationship, rail });
      toast(t.suppliers.created);
      onSaved(b);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ padding: editing ? 0 : 22, display: "flex", flexDirection: "column", gap: 16 }} noValidate>
      {editing ? <p className="ink2" style={{ fontSize: 13.5 }}>{t.suppliers.editLead}</p> : <h2>{t.suppliers.new}</h2>}
      <Field label={t.suppliers.legalName}>
        {(id) => <div className="inp"><input id={id} value={legalName} onChange={(e) => setLegalName(e.target.value)} autoComplete="organization" /></div>}
      </Field>
      <div className="grid2" style={{ gap: 12 }}>
        <Field label={t.suppliers.country} hint={t.suppliers.countryHint}>
          {(id, d) => <div className="inp"><input id={id} className="mono" maxLength={2} placeholder={t.suppliers.countryPh} value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} aria-describedby={d} /></div>}
        </Field>
        <Field label={t.suppliers.relationship}>
          {(id) => (
            <div className="inp">
              <select id={id} value={relationship} onChange={(e) => setRelationship(e.target.value as Relationship)}>
                <option value="goods">{t.suppliers.relationships.goods}</option>
                <option value="service">{t.suppliers.relationships.service}</option>
              </select>
            </div>
          )}
        </Field>
      </div>
      <Field label={t.suppliers.address}>
        {(id) => <div className="inp"><input id={id} value={address} onChange={(e) => setAddress(e.target.value)} /></div>}
      </Field>
      <Field label={t.suppliers.document} hint={kind === "bank" ? t.suppliers.documentRequiredBank : undefined}>
        {(id, d) => <div className="inp"><input id={id} className="mono" value={document} onChange={(e) => setDocument(e.target.value)} aria-describedby={d} /></div>}
      </Field>
      <div className="field">
        <span className="lbl">{t.suppliers.howReceives}</span>
        <div className="seg" role="group" aria-label={t.suppliers.howReceives}>
          <button type="button" aria-pressed={kind === "stablecoin"} onClick={() => setKind("stablecoin")}>{t.suppliers.railStable}</button>
          <button type="button" aria-pressed={kind === "bank"} onClick={() => bankRailOn && setKind("bank")} disabled={!bankRailOn} title={bankRailOn ? undefined : t.suppliers.bankRailOff}>{t.suppliers.railBank}</button>
        </div>
      </div>
      {kind === "stablecoin" ? (
        <>
          <div className="grid2" style={{ gap: 12 }}>
            <Field label={t.suppliers.asset}>
              {(id) => (
                <div className="inp">
                  <select id={id} value={asset} onChange={(e) => setAsset(e.target.value as "USDT" | "USDC")}>
                    <option value="USDT">USDT</option>
                    <option value="USDC">USDC</option>
                  </select>
                </div>
              )}
            </Field>
            <Field label={t.suppliers.network}>
              {(id) => (
                <div className="inp">
                  <select id={id} value={network} onChange={(e) => setNetwork(e.target.value as Network)}>
                    {(["tron", "ethereum", "polygon", "solana"] as Network[]).map((n) => (
                      <option key={n} value={n}>{t.suppliers.networks[n]}</option>
                    ))}
                  </select>
                </div>
              )}
            </Field>
          </div>
          <Field label={t.suppliers.walletAddress} error={addrValid === false ? fmt(t.suppliers.walletInvalid, { network: netName }) : null}>
            {(id, d) => <div className={`inp ${addrValid === false ? "err" : ""}`}><input id={id} className="mono" value={walletAddr} onChange={(e) => setWalletAddr(e.target.value.trim())} aria-describedby={d} spellCheck={false} autoCapitalize="off" /></div>}
          </Field>
          {addrValid && (
            <span className="okmark" style={{ fontSize: 12.5 }}>
              {Icon.check()}
              {fmt(t.suppliers.walletValid, { network: netName })}
            </span>
          )}
        </>
      ) : (
        <>
          <div className="grid2" style={{ gap: 12 }}>
            <Field label={t.suppliers.bankCountry}>
              {(id) => <div className="inp"><input id={id} className="mono" maxLength={2} value={bankCountry} onChange={(e) => setBankCountry(e.target.value.toUpperCase())} /></div>}
            </Field>
            <Field label={t.suppliers.scheme} hint={t.suppliers.schemeHint[scheme]}>
              {(id, d) => (
                <div className="inp">
                  <select id={id} value={scheme} onChange={(e) => setScheme(e.target.value as BankScheme)} aria-describedby={d}>
                    {schemes.map((s) => <option key={s} value={s}>{t.suppliers.schemes[s]}</option>)}
                  </select>
                </div>
              )}
            </Field>
          </div>
          <div className="row" style={{ gap: 8, fontSize: 12.5 }}>
            <Pill tone="mute">{t.suppliers.slaLabel}: {t.suppliers.schemeSla[scheme]}</Pill>
            <span className="muted">{t.suppliers.costNote}</span>
          </div>
          {[{ title: null, fields: idFields }, { title: t.suppliers.bankAddress, fields: bankFields }, { title: t.suppliers.holderSection, fields: holderFields }].map((group, gi) => group.fields.length === 0 ? null : (
            <div key={gi} className="stack s8">
              {group.title && <span className="lbl">{group.title}</span>}
              {group.fields.map((f) => {
                const err = fieldError(f);
                return (
                  <Field key={f.key} label={f.label} error={err}>
                    {(id, d) => f.select ? (
                      <div className="inp">
                        <select id={id} value={account[f.key] ?? ""} onChange={(e) => setAccount({ ...account, [f.key]: e.target.value })} aria-describedby={d}>
                          <option value="">—</option>
                          {Object.entries(f.select).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                      </div>
                    ) : (
                      <div className={`inp ${err ? "err" : ""}`}><input id={id} className={f.mono ? "mono" : ""} value={account[f.key] ?? ""} onChange={(e) => setAccount({ ...account, [f.key]: e.target.value })} aria-describedby={d} autoComplete={f.autoComplete ?? "off"} spellCheck={false} /></div>
                    )}
                  </Field>
                );
              })}
            </div>
          ))}
        </>
      )}
      {error && <div className="form-error" role="alert">{error}</div>}
      <button type="submit" className="btn full" disabled={pending}>
        {pending ? t.suppliers.submitting : editing ? t.suppliers.saveChanges : t.suppliers.submit}
      </button>
    </form>
  );
}
